import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./Notice.module.css";
import apiService from "../../../services/api";
import { setupAutoLogout } from "../../../utils/auth";

const TABS = [
    { label: "공지사항", value: "notice" },
    { label: "언론보도", value: "press" },
    { label: "세미나 & 컨퍼런스", value: "seminar" },
    { label: "연혁", value: "history" },
    { label: "조직도", value: "organization" },
    { label: "올해의 컬러", value: "year-color" },
    { label: "회원 관리", value: "user" },
];

export default function AdminNotices() {
    const [activeTab, setActiveTab] = useState("");
    const [page, setPage] = useState(1); // 페이지네이션 제거했지만 호환성을 위해 유지
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [userFilter, setUserFilter] = useState("all"); // all, pending, approved, rejected
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [rejectionReason, setRejectionReason] = useState("");
    const [showUserDetail, setShowUserDetail] = useState(false);
    const [selectedUserDetail, setSelectedUserDetail] = useState(null);
    
    // 페이지네이션 제거됨 - 모든 데이터를 한 번에 표시

    // 인증 확인 및 초기 데이터 로딩 
    useEffect(() => {
        const token = localStorage.getItem("adminToken");
        if (!token) {
            navigate("/admin/login");
            return;
        }
        // TODO: API 서비스 연동 시 주석 해제
         const cleanup = setupAutoLogout(() => {
             navigate("/admin/login");
         });
         apiService.setTokenExpiredCallback(() => {
             navigate("/admin/login");
         });
         return () => {
             cleanup();
         };
    }, [navigate]);

    // URL 파라미터에서 탭 값 읽기
    useEffect(() => {
        const initialTab = searchParams.get("tab") || "notice";
        if (initialTab) {
                setActiveTab(initialTab);
                setPage(1); // 페이지 초기화
        }
    }, [searchParams]);

    // 탭 변경 시 데이터 새로고침
    useEffect(() => {
        console.log("activeTab", activeTab);
        const token = localStorage.getItem("adminToken");
        if (token) {
            fetchData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, userFilter]); // userFilter 변경 시에도 데이터 새로고침

    // 데이터 가져오기
    const fetchData = async () => {
        setIsLoading(true);
        setError("");
        console.log("fetchData", activeTab);
        try {
            let response;
            // API 서비스 연동
            switch (activeTab) {
                case "notice":
                    response = await apiService.getNotices(1, 1000);
                    break;
                case "press":
                    response = await apiService.getPressList(1, 1000);
                    break;
                case "seminar":
                    response = await apiService.getSeminars(1, 1000);
                    break;
                case "history":
                    response = await apiService.getHistory(1, 1000);
                    break;
                case "organization":
                    response = await apiService.getOrganization();
                    break;
                case "year-color":
                    response = await apiService.getYearColor();
                    break;
                case "user":
                    // 사용자 목록은 필터에 따라 다르게 가져오기
                    if (userFilter === "pending") {
                        response = await apiService.getPendingUsers();
                    } else {
                        response = await apiService.getAllUsers();
                    }
                    break;
                default:
                    response = [];
            }
            
            // 응답 데이터 구조 확인 및 안전한 처리
            console.log(`${activeTab} API response:`, response);
            console.log("response", response);
            
            if (response && Array.isArray(response)) {
                setData(response);
            } else if (response && response.data && Array.isArray(response.data)) {
                setData(response.data);
            } else if (response && Array.isArray(response.items)) {
                setData(response.items);
            } else if (response && response.notices && Array.isArray(response.notices)) {
                setData(response.notices);
            } else if (response && response.presses && Array.isArray(response.presses)) {
                setData(response.presses);
            } else if (response && response.histories && Array.isArray(response.histories)) {
                setData(response.histories);
            } else if (response && response.seminars && Array.isArray(response.seminars)) {
                setData(response.seminars);
            } else if (activeTab === "organization") {
                // 조직도는 단일 객체이므로 배열로 감싸서 처리
                setData(response ? [response] : []);
            } else if (activeTab === "year-color") {
                // 올해의 컬러는 연도별 객체이므로 배열로 변환
                if (response && typeof response === 'object') {
                    const yearColorArray = Object.keys(response).map(year => ({
                        year: parseInt(year),
                        ...response[year]
                    }));
                    setData(yearColorArray);
                } else {
                    setData([]);
                }
            } else {
                console.warn(`Unexpected response format for ${activeTab}:`, response);
                setData([]);
            }
        } catch (error) {
            setError("데이터를 불러오는데 실패했습니다.");
            console.error("Fetch error:", error);
            setData([]);
        } finally {
            setIsLoading(false);
        }
    };

    // 아이템 삭제
    const handleDelete = async (id) => {
        if (!id) {
            alert('삭제할 항목을 찾을 수 없습니다.');
            return;
        }
        
     /*    if (!confirm("정말 삭제하시겠습니까?")) return; */
        
        try {
            switch (activeTab) {
                case "notice":
                    await apiService.deleteNotice(id);
                    break;
                case "press":
                    await apiService.deletePress(id);
                    break;
                case "seminar":
                    await apiService.deleteSeminar(id);
                    break;
                case "history":
                    await apiService.deleteHistory(id);
                    break;
                case "year-color":
                    // 올해의 컬러는 year를 사용
                    if (!id || isNaN(id)) {
                        alert('유효하지 않은 연도입니다.');
                        return;
                    }
                    await apiService.deleteYearColor(id);
                    break;
                case "user":
                    if (!window.confirm("정말 삭제하시겠습니까?\n삭제된 회원 정보는 복구할 수 없습니다.")) return;
                    await apiService.deleteUser(id);
                    break;
                case "organization":
                    // 조직도는 삭제 불가, 수정만 가능
                    break;
                default:
                    alert('알 수 없는 탭입니다.');
                    return;
            }
            alert('삭제되었습니다.');
            fetchData(); // 데이터 새로고침
        } catch (error) {
            console.error('Delete error:', error);
            alert(`삭제에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
        }
    };

    // 새 아이템 등록
    const handleNew = () => {
        const formRoutes = {
            notice: "/admin/notices/notice-form",
            press: "/admin/notices/press-form",
            seminar: "/admin/notices/seminar-form",
            history: "/admin/notices/history-form",
            organization: "/admin/notices/organization-form",
            "year-color": "/admin/notices/year-color-form"
        };
        navigate(formRoutes[activeTab]);
    };

    // 아이템 수정
    const handleEdit = (item) => {
        const formRoutes = {
            notice: `/admin/notices/notice-form?id=${item.id}`,
            press: `/admin/notices/press-form?id=${item.id}`,
            seminar: `/admin/notices/seminar-form?id=${item.id}`,
            history: `/admin/notices/history-form?id=${item.id}`,
            organization: `/admin/notices/organization-form${item.id ? `?id=${item.id}` : ''}`,
            "year-color": `/admin/notices/year-color-form?year=${item.year || item.id}`
        };
        navigate(formRoutes[activeTab]);
    };

    // 사용자 승인
    const handleApproveUser = async (userId) => {
     /*    if (!confirm("이 사용자를 승인하시겠습니까?")) return; */
        try {
            await apiService.approveUser(userId);
            alert("승인되었습니다.");
            fetchData();
        } catch (error) {
            console.error('Approve error:', error);
            alert(`승인에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
        }
    };

    // 사용자 거부 모달 열기
    const handleRejectUserClick = (user) => {
        setSelectedUser(user);
        setRejectionReason("");
        setShowRejectModal(true);
    };

    // 사용자 거부 실행
    const handleRejectUser = async () => {
        if (!rejectionReason.trim()) {
            alert("거부 사유를 입력해주세요.");
            return;
        }
        try {
            await apiService.rejectUser(selectedUser.id, rejectionReason);
            alert("거부되었습니다.");
            setShowRejectModal(false);
            setSelectedUser(null);
            setRejectionReason("");
            fetchData();
        } catch (error) {
            console.error('Reject error:', error);
            alert(`거부에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
        }
    };

    // 사용자 상세 보기
    const handleViewUserDetail = (user) => {
        setSelectedUserDetail(user);
        setShowUserDetail(true);
    };

    // 승인 완료된 회원을 다시 승인 대기 상태로 변경
    const handleResetUserStatus = async (userId) => {
        /* if (!confirm("이 사용자를 승인 대기 상태로 변경하시겠습니까?")) return; */
        try {
            await apiService.resetUserStatus(userId);
            alert("승인 대기 상태로 변경되었습니다.");
            fetchData();
        } catch (error) {
            console.error('Reset status error:', error);
            alert(`상태 변경에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
        }
    };

    // 탭별 데이터 - 페이지네이션 제거하고 모든 데이터 표시
    const safeData = Array.isArray(data) ? data : [];
    const pagedData = safeData; // 모든 데이터를 그대로 표시
    const totalPages = 1; // 페이지네이션 숨김

    // 필터링된 사용자 데이터
    const filteredUserData = activeTab === "user" 
        ? (userFilter === "all" 
            ? pagedData 
            : pagedData.filter(user => user.status === userFilter.toUpperCase()))
        : [];

    // 탭 클릭 시 페이지 1로 초기화
    const handleTab = (tab) => {
        setActiveTab(tab);
        setPage(1);
        navigate(`/admin/notices?tab=${tab}`); // URL 파라미터 업데이트
    };

    // 로그아웃
    const handleLogout = () => {
        localStorage.removeItem("adminToken");
        // TODO: API 서비스 연동 시 주석 해제
        // apiService.logout();
        navigate("/admin/login");
    };

    if (isLoading) {
        return (
            <div className={styles.noticeWrap}>
                <div className={styles.SubPageWidth}>
                    <div className={styles.loading}>로딩 중...</div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.noticeWrap}>
            <div className={styles.SubPageWidth}>
                {/* 헤더 */}
                <div className={styles.header}>
                    <h1>관리자 페이지</h1>
                    <button onClick={handleLogout} className={styles.logoutBtn}>
                        로그아웃
                    </button>
                </div>

                <div className={styles.tabWrap}>
                    {TABS.map((tab) => (
                        <button
                            key={tab.value}
                            type="button"
                            className={`${styles.tab} ${
                                activeTab === tab.value ? styles.active : ""
                            }`}
                            onClick={() => handleTab(tab.value)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* 등록버튼*/}
                {activeTab !== "organization" && activeTab !== "year-color" && activeTab !== "user" && (
                    <div className={styles.newBtn}>
                        <button onClick={handleNew}>+ 등록</button>
                    </div>
                )}
                {activeTab === "organization" && (
                    <div className={styles.newBtn}>
                        <button onClick={() => handleEdit(pagedData[0] || {})}>이미지 수정</button>
                    </div>
                )}
                {activeTab === "year-color" && (
                    <div className={styles.newBtn}>
                        <button onClick={handleNew}>+ 등록</button>
                    </div>
                )}
                {/* 회원 관리 필터 */}
                {activeTab === "user" && (
                    <div className={styles.userFilter}>
                        <button 
                            className={userFilter === "all" ? styles.activeFilter : ""}
                            onClick={() => setUserFilter("all")}
                        >
                            전체
                        </button>
                        <button 
                            className={userFilter === "pending" ? styles.activeFilter : ""}
                            onClick={() => setUserFilter("pending")}
                        >
                            승인 대기
                        </button>
                        <button 
                            className={userFilter === "approved" ? styles.activeFilter : ""}
                            onClick={() => setUserFilter("approved")}
                        >
                            승인 완료
                        </button>
                        <button 
                            className={userFilter === "rejected" ? styles.activeFilter : ""}
                            onClick={() => setUserFilter("rejected")}
                        >
                            거부됨
                        </button>
                    </div>
                )}

                {/* 에러 메시지 */}
                {error && <div className={styles.error}>{error}</div>}

                {/* 카드 리스트 */}
                <div className={activeTab === "user" ? styles.userGrid : styles.grid}>
                    {activeTab === "user" ? (
                        filteredUserData.length === 0 ? (
                            <div className={styles.empty}>사용자가 없습니다.</div>
                        ) : (
                            filteredUserData.map((user, i) => (
                                <div className={styles.userCard} key={user.id || i}>
                                    <div className={styles.userInfo}>
                                        <div className={styles.userName}>{user.name}</div>
                                        <div className={styles.userEmail}>{user.email}</div>
                                        <div className={styles.userPhone}>{user.phone || "-"}</div>
                                        {user.address && (
                                            <div className={styles.userAddress}>{user.address}</div>
                                        )}
                                        <div className={styles.userDate}>
                                            가입일: {new Date(user.createdAt).toLocaleDateString('ko-KR')}
                                        </div>
                                        <div className={styles.userStatus}>
                                            상태: <span className={
                                                user.status === "PENDING" ? styles.statusPending :
                                                user.status === "APPROVED" ? styles.statusApproved :
                                                styles.statusRejected
                                            }>
                                                {user.status === "PENDING" ? "승인 대기" :
                                                 user.status === "APPROVED" ? "승인 완료" :
                                                 "거부됨"}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles.userActions}>
                                        {user.status === "PENDING" && (
                                            <>
                                                <button 
                                                    className={styles.approveBtn}
                                                    onClick={() => handleApproveUser(user.id)}
                                                >
                                                    승인
                                                </button>
                                                <button 
                                                    className={styles.rejectBtn}
                                                    onClick={() => handleRejectUserClick(user)}
                                                >
                                                    거부
                                                </button>
                                            </>
                                        )}
                                        {user.status === "APPROVED" && (
                                            <button 
                                                className={styles.resetBtn}
                                                onClick={() => handleResetUserStatus(user.id)}
                                            >
                                                승인 대기로 변경
                                            </button>
                                        )}
                                        <button 
                                            className={styles.detailBtn}
                                            onClick={() => handleViewUserDetail(user)}
                                        >
                                            상세보기
                                        </button>
                                        <button 
                                            className={styles.deleteBtn}
                                            onClick={() => handleDelete(user.id)}
                                        >
                                            삭제
                                        </button>
                                    </div>
                                </div>
                            ))
                        )
                    ) : pagedData.length === 0 ? (
                        <div className={styles.empty}>게시물이 없습니다.</div>
                    ) : (
                        pagedData.map((item, i) => (
                            <div className={styles.card} key={item.year || item.id || i}>
                                {/* 조직도 탭일 때는 전용 카드 구조 */}
                                {activeTab === "organization" ? (
                                    <>
                                        <img
                                            src={item.imageUrl || item.image || "/image/about_organization.png"}
                                            alt="조직도"
                                            className={styles.cardImg}
                                        />
                                        <div className={styles.cardText}>
                                            <div className={styles.title}>조직도</div>
                                            <div className={styles.date}>
                                                <div className={styles.editBtn}>
                                                    <button onClick={() => handleEdit(item)}>수정</button>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : activeTab === "year-color" ? (
                                    <>
                                        <img
                                            src={item.bannerImage || "/image/20160107000821_0.jpg"}
                                            alt={`${item.year}년 컬러`}
                                            className={styles.cardImg}
                                        />
                                        <div className={styles.cardText}>
                                            <div className={styles.title}>
                                                {item.year}년 - {item.colorName}
                                            </div>
                                            <div className={styles.date}>
                                                <div>
                                                    <span style={{ color: item.hex }}>●</span> {item.hex}
                                                </div>
                                                <div className={styles.editBtn}>
                                                    <button onClick={() => handleEdit(item)}>수정</button>|
                                                    <button onClick={() => {
                                                        if (item.year) {
                                                            handleDelete(item.year);
                                                        } else {
                                                            alert('연도 정보가 없습니다.');
                                                        }
                                                    }}>삭제</button>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* 연혁일 때는 이미지 숨김 */}
                                        {activeTab !== "history" && (
                                            <img
                                                src={item.imageUrl || "/image/20160107000821_0.jpg"}
                                                alt=""
                                                className={styles.cardImg}
                                            />
                                        )}
                                        <div className={styles.cardText}>
                                            {/* 연혁일 때는 title 숨김 */}
                                            {activeTab !== "history" && (
                                                <div className={styles.title}>
                                                    {item.title}
                                                </div>
                                            )}
                                            <div className={styles.date}>
                                                <div>
                                                    {activeTab === "history" 
                                                        ? `${item.year}년`
                                                        : new Date(item.date).toLocaleDateString('ko-KR')
                                                    }
                                                </div>
                                                <div className={styles.editBtn}>
                                                    <button onClick={() => handleEdit(item)}>수정</button>|
                                                    <button onClick={() => handleDelete(item.id)}>삭제</button>
                                                </div>
                                            </div>
                                            {/* 연혁일 때만 내용 미리보기 표시 */}
                                            {activeTab === "history" && item.description && (
                                                <div className={styles.description}>
                                                    {item.description.length > 50 
                                                        ? `${item.description.substring(0, 50)}...` 
                                                        : item.description
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* 페이지네이션 */}
                {totalPages > 1 && (
                    <div className={styles.pagination}>
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                className={`${styles.pageBtn} ${
                                    page === i + 1 ? styles.active : ""
                                }`}
                                onClick={() => setPage(i + 1)}
                                type="button"
                            >
                                {i + 1}
                            </button>
                        ))}
                    </div>
                )}

                {/* 거부 사유 입력 모달 */}
                {showRejectModal && (
                    <div className={styles.modalOverlay} onClick={() => setShowRejectModal(false)}>
                        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                            <h3>사용자 거부</h3>
                            <p>거부 사유를 입력해주세요.</p>
                            <textarea
                                className={styles.rejectTextarea}
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                placeholder="거부 사유를 입력하세요..."
                                rows={5}
                            />
                            <div className={styles.modalActions}>
                                <button 
                                    className={styles.cancelBtn}
                                    onClick={() => {
                                        setShowRejectModal(false);
                                        setSelectedUser(null);
                                        setRejectionReason("");
                                    }}
                                >
                                    취소
                                </button>
                                <button 
                                    className={styles.confirmBtn}
                                    onClick={handleRejectUser}
                                >
                                    거부
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 사용자 상세 보기 모달 */}
                {showUserDetail && selectedUserDetail && (
                    <div className={styles.modalOverlay} onClick={() => setShowUserDetail(false)}>
                        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                            <h3>사용자 상세 정보</h3>
                            <div className={styles.userDetail}>
                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>이름:</span>
                                    <span>{selectedUserDetail.name}</span>
                                </div>
                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>이메일:</span>
                                    <span>{selectedUserDetail.email}</span>
                                </div>
                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>전화번호:</span>
                                    <span>{selectedUserDetail.phone || "-"}</span>
                                </div>
                                {selectedUserDetail.address && (
                                    <div className={styles.detailRow}>
                                        <span className={styles.detailLabel}>주소:</span>
                                        <span>{selectedUserDetail.address}</span>
                                    </div>
                                )}
                                {selectedUserDetail.referralCode && (
                                    <div className={styles.detailRow}>
                                        <span className={styles.detailLabel}>추천인 코드:</span>
                                        <span>{selectedUserDetail.referralCode}</span>
                                    </div>
                                )}
                                {selectedUserDetail.referralSource && (
                                    <div className={styles.detailRow}>
                                        <span className={styles.detailLabel}>가입 경로:</span>
                                        <span>{selectedUserDetail.referralSource}</span>
                                    </div>
                                )}
                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>상태:</span>
                                    <span className={
                                        selectedUserDetail.status === "PENDING" ? styles.statusPending :
                                        selectedUserDetail.status === "APPROVED" ? styles.statusApproved :
                                        styles.statusRejected
                                    }>
                                        {selectedUserDetail.status === "PENDING" ? "승인 대기" :
                                         selectedUserDetail.status === "APPROVED" ? "승인 완료" :
                                         "거부됨"}
                                    </span>
                                </div>
                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>가입일시:</span>
                                    <span>{new Date(selectedUserDetail.createdAt).toLocaleString('ko-KR')}</span>
                                </div>
                                {selectedUserDetail.updatedAt && (
                                    <div className={styles.detailRow}>
                                        <span className={styles.detailLabel}>수정일시:</span>
                                        <span>{new Date(selectedUserDetail.updatedAt).toLocaleString('ko-KR')}</span>
                                    </div>
                                )}
                            </div>
                            <div className={styles.modalActions}>
                                <button 
                                    className={styles.closeBtn}
                                    onClick={() => {
                                        setShowUserDetail(false);
                                        setSelectedUserDetail(null);
                                    }}
                                >
                                    닫기
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

